import React, { useEffect, useMemo, useState } from 'react';
import Layout, { EmptyState, Pager, Spinner, StatTile } from '../components/Layout';
import Modal from '../components/Modal';
import { CardBadges, EditTypeChip, ReceptionStamp } from '../components/StatusBadge';
import CaseNotes from '../components/CaseNotes';
import VoiceRecorder from '../components/VoiceRecorder';
import AudioNote from '../components/AudioNote';
import { PrescriptionStrip } from '../components/Prescription';
import { useSocket } from '../context/SocketContext';
import { useCaseAction, useCases } from '../hooks/useCases';
import { cases as casesApi, stats as statsApi } from '../utils/api';
import { formatDateTime, formatDuration, formatNumber, isolate } from '../utils/format';

const TABS = [
  { id: 'queue', label: 'در انتظار بازبینی', status: 'technical_review' },
  { id: 'revision', label: 'برگشت‌خورده', status: 'revision_requested' },
  { id: 'done', label: 'تأییدشده‌ها', status: 'report_completed,closed' },
];

export default function TechnicalDashboard() {
  const [tab, setTab] = useState('queue');
  const [rejecting, setRejecting] = useState(null);
  const [history, setHistory] = useState(null);
  const { caseVersion } = useSocket();
  const [summary, setSummary] = useState(null);

  const activeTab = TABS.find((t) => t.id === tab);
  const params = useMemo(() => ({ status: activeTab.status }), [activeTab.status]);
  const { cases, count, page, setPage, pageCount, pageSize, loading, error, reload } =
    useCases(params);
  const { run, pendingId } = useCaseAction(reload);

  useEffect(() => {
    statsApi.get().then(({ data }) => setSummary(data)).catch(() => setSummary(null));
  }, [caseVersion]);

  return (
    <Layout
      title="میز مسئول فنی"
      subtitle="بازبینی گزارش‌ها پیش از تحویل به بیمار"
      tabs={TABS.map((t) => ({ ...t, count: t.id === tab ? count : undefined }))}
      activeTab={tab}
      onTabChange={setTab}
    >
      {summary && (
        <div className="tiles">
          <StatTile
            label="در انتظار بازبینی"
            value={formatNumber(summary.awaiting_review)}
            tone={summary.awaiting_review > 0 ? 'warning' : 'default'}
          />
          <StatTile label="نزد ادیتور برای اصلاح" value={formatNumber(summary.in_revision)} tone="brand" />
          <StatTile label="تأییدشده توسط من" value={formatNumber(summary.approved_by_me)} tone="success" />
          <StatTile label="مجموع نوبت‌های اصلاح" value={formatNumber(summary.revisions_requested_total)} />
        </div>
      )}

      {loading && <Spinner />}
      {error && <p className="alert alert--danger">{error}</p>}

      {!loading && !error && cases.length === 0 && (
        <EmptyState
          icon="🔎"
          title={
            tab === 'queue'
              ? 'گزارشی در انتظار بازبینی نیست.'
              : tab === 'revision'
              ? 'پرونده‌ای در دست اصلاح نیست.'
              : 'هنوز گزارشی تأیید نکرده‌اید.'
          }
          hint={tab === 'queue' ? 'به‌محض ارسال گزارش توسط ادیتور، اینجا ظاهر می‌شود.' : undefined}
        />
      )}

      <div className="cards">
        {cases.map((item) => (
          <article
            key={item.id}
            className={`card ${item.status === 'technical_review' ? 'card--review' : ''} ${item.is_urgent ? 'card--urgent' : ''}`}
          >
            <header className="card__head">
              <div>
                <h3 className="card__title"><bdi>{item.name}</bdi></h3>
                <p className="card__meta">
                  <bdi>{item.doctor}</bdi> · نسخه <span className="num">{item.order_number}</span>
                </p>
              </div>
              <CardBadges item={item} />
            </header>

            <div className="card__row">
              <EditTypeChip code={item.edit_type_code} name={item.edit_type_name} />
              {item.revision_count > 0 && (
                <span className="pill pill--warn">
                  {formatNumber(item.revision_count)} نوبت اصلاح
                </span>
              )}
            </div>

            <div className="card__row">
              <ReceptionStamp at={item.reception_time} />
            </div>

            <dl className="card__facts">
              <div>
                <dt>ادیتور</dt>
                <dd><bdi>{item.editor_name}</bdi></dd>
              </div>
              <div>
                <dt>زمان پروژه</dt>
                <dd>{formatDuration(item.billable_seconds)}</dd>
              </div>
              <div>
                <dt>ارسال</dt>
                <dd>{formatDateTime(item.end_time)}</dd>
              </div>
            </dl>

            <PrescriptionStrip items={item.prescriptions} />

            <CaseNotes caseId={item.id} notes={item.notes} onChanged={reload} />

            <div className="filelinks">
              {item.word_cloud_url && (
                <a href={item.word_cloud_url} target="_blank" rel="noreferrer" className="cloud-link">
                  📄 فایل Word
                </a>
              )}
              {item.jpeg_cloud_url && (
                <a href={item.jpeg_cloud_url} target="_blank" rel="noreferrer" className="cloud-link">
                  🖼️ تصویر گزارش
                </a>
              )}
              {item.scan_cloud_url && (
                <a href={item.scan_cloud_url} target="_blank" rel="noreferrer" className="cloud-link">
                  ☁️ اسکن اصلی
                </a>
              )}
            </div>

            <footer className="card__foot">
              {item.revision_count > 0 && (
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setHistory(item)}>
                  📜 سوابق
                </button>
              )}
              {item.status === 'technical_review' && (
                <>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={pendingId === item.id}
                    onClick={() => setRejecting(item)}
                  >
                    ↩️ عدم تأیید
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={pendingId === item.id}
                    onClick={() => run(item.id, () => casesApi.approve(item.id), 'گزارش تأیید شد.')}
                  >
                    ✅ تأیید نهایی
                  </button>
                </>
              )}
            </footer>
          </article>
        ))}
      </div>

      {!loading && cases.length > 0 && pageCount === 1 && (
        <p className="list-footnote">{formatNumber(count)} پرونده در این فهرست</p>
      )}

      <Pager
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        count={count}
        onChange={setPage}
      />

      <RejectModal
        item={rejecting}
        pending={pendingId === rejecting?.id}
        onClose={() => setRejecting(null)}
        onSubmit={async (payload) => {
          const result = await run(
            rejecting.id,
            () => casesApi.requestRevision(rejecting.id, payload),
            'پرونده برای اصلاح به ادیتور بازگشت.'
          );
          if (result) setRejecting(null);
        }}
      />

      <HistoryModal item={history} onClose={() => setHistory(null)} />
    </Layout>
  );
}

function RejectModal({ item, pending, onClose, onSubmit }) {
  const [text, setText] = useState('');
  const [recording, setRecording] = useState(null);

  useEffect(() => {
    setText('');
    setRecording(null);
  }, [item?.id]);

  if (!item) return null;

  const valid = text.trim().length > 0 || recording?.blob;

  return (
    <Modal
      open
      title="عدم تأیید و بازگرداندن به ادیتور"
      onClose={onClose}
      width={560}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            انصراف
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!valid || pending}
            onClick={() =>
              onSubmit({
                text: text.trim(),
                audio: recording?.blob ?? null,
                audioSeconds: recording?.seconds ?? null,
              })
            }
          >
            {pending ? 'در حال ارسال…' : 'بازگرداندن به ادیتور'}
          </button>
        </>
      }
    >
      <p className="modal__lead">
        بیمار <strong><bdi>{item.name}</bdi></strong> — ادیتور: <bdi>{item.editor_name}</bdi>
      </p>

      <div className="field">
        <label htmlFor="reject-text">یادداشت متنی</label>
        <textarea
          id="reject-text"
          rows={4}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="چه چیزی باید اصلاح شود؟"
        />
      </div>

      <VoiceRecorder onChange={setRecording} disabled={pending} />

      {!valid && (
        <p className="alert alert--warning">
          دست‌کم یکی از یادداشت متنی یا صوتی الزامی است.
        </p>
      )}

      <p className="form__note">
        از این لحظه تا تأیید نهایی، به زمان پروژه اضافه می‌شود و کارانه‌ی ادیتور
        را کاهش می‌دهد.
      </p>
    </Modal>
  );
}

function HistoryModal({ item, onClose }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!item) return;
    setLoading(true);
    casesApi
      .reviewNotes(item.id)
      .then(({ data }) => setNotes(data))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [item]);

  if (!item) return null;

  return (
    <Modal open title={`سوابق بازبینی «${isolate(item.name)}»`} onClose={onClose} width={560}>
      {loading ? (
        <Spinner />
      ) : notes.length === 0 ? (
        <EmptyState icon="📜" title="یادداشتی ثبت نشده است." />
      ) : (
        <ul className="notes">
          {notes.map((note) => (
            <li key={note.id} className="note">
              <header className="note__head">
                <strong>نوبت {formatNumber(note.round_number)}</strong>
                <span className="muted">
                  {note.reviewer_name} · <time>{formatDateTime(note.created_at)}</time>
                </span>
              </header>
              {note.text && <p className="note__text"><bdi>{note.text}</bdi></p>}
              {note.has_audio && <AudioNote url={note.audio_url} seconds={note.audio_seconds} />}
              <p className="note__status">
                {note.resolved_at
                  ? `اصلاح و ارسال دوباره: ${formatDateTime(note.resolved_at)}`
                  : 'هنوز اصلاح نشده'}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
